// ServicesPage.jsx
import React, { useEffect, useRef } from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Activity,
  Users,
  Calendar,
  Building,
  Heart,
  Scissors,
  Microscope,
  Baby,
  Scan,
  HeartPulse,
  Stethoscope,
} from 'lucide-react';

export const ServicesPage = () => {
  const servicesRef = useRef(null);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('slide-up');
            }, index * 100);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = servicesRef.current?.querySelectorAll('.service-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Activity,
      title: 'Emergency Services',
      description: '24/7 emergency medical care with rapid response teams and advanced life support systems for critical situations.',
      color: 'from-red-500 to-red-700',
      features: ['24/7 Availability', 'Trauma Care', 'Emergency Surgery', 'Critical Care']
    },
    {
      icon: Users,
      title: 'Out Patient Department',
      description: 'Comprehensive medical consultations, diagnostics, and treatments without hospital admission.',
      color: 'from-blue-500 to-blue-700',
      features: ['Medical Consultations', 'Health Check-ups', 'Follow-up Visits', 'Specialist Referrals']
    },
    {
      icon: Calendar,
      title: 'Daycare Services',
      description: 'Same-day medical procedures and surgeries that allow you to return home on the same day.',
      color: 'from-green-500 to-green-700',
      features: ['Minor Surgeries', 'Endoscopies', 'Chemotherapy', 'Day Procedures']
    },
    {
      icon: Building,
      title: 'In Patient Department',
      description: 'Comprehensive住院 care with comfortable rooms and round-the-clock medical supervision.',
      color: 'from-purple-500 to-purple-700',
      features: ['Private Rooms', '24/7 Nursing Care', 'Specialist Rounds', 'Post-operative Care']
    },
    {
      icon: Heart,
      title: 'ICU (Intensive Care Unit)',
      description: 'Advanced critical care with state-of-the-art monitoring equipment and specialized medical staff.',
      color: 'from-orange-500 to-orange-700',
      features: ['Ventilator Support', 'Cardiac Monitoring', 'Critical Care Specialists', 'Advanced Life Support']
    },
    {
      icon: Scissors,
      title: 'Operation Theatre',
      description: 'Modern surgical suites equipped with advanced technology for various surgical procedures.',
      color: 'from-indigo-500 to-indigo-700',
      features: ['Laminar Air Flow', 'Advanced Anesthesia', 'Surgical ICU', 'Sterile Environment']
    },
    {
      icon: Microscope,
      title: 'Laboratory Services',
      description: 'Comprehensive diagnostic testing with advanced laboratory equipment and accurate results.',
      color: 'from-teal-500 to-teal-700',
      features: ['Blood Tests', 'Pathology', 'Microbiology', 'Biochemistry']
    },
    {
      icon: Baby,
      title: 'Labour & Delivery Unit',
      description: 'Specialized care for expecting mothers with modern delivery suites and neonatal care.',
      color: 'from-pink-500 to-pink-700',
      features: ['Normal Delivery', 'C-Section', 'Neonatal Care', 'Pain Management']
    },
    {
      icon: Scan,
      title: 'Digital X-ray',
      description: 'Advanced digital imaging services for accurate diagnosis with minimal radiation exposure.',
      color: 'from-cyan-500 to-cyan-700',
      features: ['Digital Imaging', 'Low Radiation', 'Quick Results', 'Various Body Parts']
    },
    {
      icon: HeartPulse,
      title: 'Family Planning & Reproductive Health (MTP)',
      description: 'Comprehensive reproductive health services including counseling and medical procedures.',
      color: 'from-rose-500 to-rose-700',
      features: ['MTP Services', 'Counseling', 'Contraception', 'Reproductive Health']
    },
    {
      icon: Stethoscope,
      title: 'ECG Services',
      description: 'Electrocardiogram testing for heart health assessment and cardiac condition diagnosis.',
      color: 'from-emerald-500 to-emerald-700',
      features: ['Resting ECG', 'Stress Test', 'Heart Monitoring', 'Cardiac Assessment']
    }
  ];

  return (
    <>
      <Navbar />
      <section className="pt-24 pb-16 bg-[hsl(var(--background))]" ref={servicesRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full">
              <Activity className="w-5 h-5 text-[hsl(var(--primary))]" />
              <span className="text-sm font-semibold text-[hsl(var(--primary))]">
                Our Medical Services
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))]">
              Comprehensive Healthcare
              <span className="block mt-2 text-gradient">Services</span>
            </h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              We provide a complete range of medical services with modern facilities, 
              experienced healthcare professionals, and compassionate care for all your health needs.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card
                key={index}
                className="service-card group hover:shadow-[var(--shadow-xl)] transition-all duration-300 hover:scale-105 opacity-0 border-none overflow-hidden bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--muted))]">
                <CardHeader className="space-y-4">
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-[var(--shadow-md)] group-hover:shadow-[var(--shadow-lg)] transition-all duration-300 group-hover:scale-110`}
                  >
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))] transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-[hsl(var(--muted-foreground))] mb-4">
                    {service.description}
                  </CardDescription>
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-[hsl(var(--foreground))]">Key Features:</h4>
                    <ul className="text-sm text-[hsl(var(--muted-foreground))] space-y-1">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-[hsl(var(--primary))] rounded-full"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Information */}
          <div className="mt-16 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">Need Emergency Care?</h3>
            <p className="text-lg mb-6 opacity-90">
              Our emergency services are available 24/7 with experienced medical professionals ready to assist you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:02067527445"
                className="bg-white text-[hsl(var(--primary))] px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
              >
                Call Emergency: 02067527445
              </a>
              <button
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-[hsl(var(--primary))] transition-colors"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};